import i18next from 'i18next'
import React from 'react'
import ReactToPrint from 'react-to-print'
import { downloadCsv } from '../../utils'
import { HighlightedText } from '../HighlightedText'
import { BasicButton, Dropdown, SearchContainer } from '..'
import { DownloadIcon, MoreVertIcon, PlayIcon, PrintIcon, RefreshIcon } from '../Icons'
import css from './DataTable.module.scss'
import toolbarCss from './DataTableToolbar.module.scss'
import { DataTablePagination } from './DataTablePagination'
import type { DataTableProps } from './types'
import type { ColumnDef, ColumnRuntime, RowRuntime, TableRuntime } from './impl/columns'
import type {
	ColumnFilterClause,
	ColumnFilterGroup,
	ColumnId,
	FilterMode,
	FilterOperator,
	FilterState,
	PaginationMode,
	RowKey,
	SortMode,
	SortState
} from './impl/primitives'
import type { StateChangeMeta, TableState } from './impl/state'
import type { DataSource, FetchContext, TableQuery, TableQueryResult } from './impl/query'

const defaultPageSizes = [10, 20, 50, 80]
const defaultMinResizableWidthPx = 80

type IndexedRow<T> = {
	original: T
	baseIndex: number
}

const defaultFilterState: FilterState = {
	global: { enabled: false },
	column: { op: 'and', clauses: [] }
}

const defaultTableState: TableState = {
	sort: [],
	filter: defaultFilterState,
	pagination: { mode: 'none' },
	selection: { mode: 'none' },
	grouping: { by: [], enabled: false },
	expansion: { expanded: new Set() },
	columnOrder: [],
	columnPinning: { left: [], right: [] },
	columnSizing: {},
	columnVisibility: {},
	density: { density: 'standard' }
}

const normalizeFilterState = (filter: FilterState): FilterState => {
	let global: FilterState['global'] = { enabled: false }
	if (filter.global.enabled) {
		const query = String(filter.global.query || '').trim()
		if (query) {
			global = { ...filter.global, enabled: true, query }
		}
	}

	return {
		...filter,
		global
	}
}

const areSetsEqual = <T,>(a: Set<T> | undefined, b: Set<T> | undefined): boolean => {
	if (a === b) return true
	if (!a || !b) return false
	if (a.size !== b.size) return false
	const values = Array.from(a)
	for (let i = 0; i < values.length; i += 1) {
		const value = values[i]
		if (!b.has(value)) return false
	}
	return true
}

const areSelectionStatesEqual = (a: TableState['selection'], b: TableState['selection']): boolean => {
	if (a.mode !== b.mode) return false
	if (a.mode === 'none') return true
	if (a.mode === 'single' && b.mode === 'single') return a.selectedRowId === b.selectedRowId
	if (a.mode === 'multiple' && b.mode === 'multiple') {
		return a.allSelected === b.allSelected && areSetsEqual(a.selectedRowIds, b.selectedRowIds) && areSetsEqual(a.excludedRowIds, b.excludedRowIds)
	}
	return false
}

const areExpansionStatesEqual = (a: TableState['expansion'], b: TableState['expansion']): boolean =>
	a.allExpanded === b.allExpanded && areSetsEqual(a.expanded, b.expanded)

const isJsonEqual = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b)

const normalizeNextStateForSlice = (next: TableState, slice: StateChangeMeta['slice']): TableState => {
	if (slice !== 'filter') return next
	return {
		...next,
		filter: normalizeFilterState(next.filter)
	}
}

const isSameSliceState = (prev: TableState, next: TableState, slice: StateChangeMeta['slice']): boolean => {
	switch (slice) {
		case 'sort':
			return isJsonEqual(prev.sort, next.sort)
		case 'filter':
			return isJsonEqual(normalizeFilterState(prev.filter), normalizeFilterState(next.filter))
		case 'pagination':
			return isJsonEqual(prev.pagination, next.pagination)
		case 'selection':
			return areSelectionStatesEqual(prev.selection, next.selection)
		case 'grouping':
			return isJsonEqual(prev.grouping, next.grouping)
		case 'expansion':
			return areExpansionStatesEqual(prev.expansion, next.expansion)
		case 'columnOrder':
			return isJsonEqual(prev.columnOrder, next.columnOrder)
		case 'columnPinning':
			return isJsonEqual(prev.columnPinning, next.columnPinning)
		case 'columnSizing':
			return isJsonEqual(prev.columnSizing, next.columnSizing)
		case 'columnVisibility':
			return isJsonEqual(prev.columnVisibility, next.columnVisibility)
		case 'density':
			return isJsonEqual(prev.density, next.density)
		case 'unknown':
			return isJsonEqual(prev, next)
		default:
			return false
	}
}

const getGlobalQuery = (filter: FilterState | undefined): string => {
	if (!filter) return ''
	if (!filter.global || filter.global.enabled === false) return ''
	return (filter.global.query || '').trim()
}

const setGlobalQuery = (prev: FilterState, query: string): FilterState => ({
	...prev,
	global:
		query && query.trim()
			? {
					enabled: true,
					query,
					...(prev.global.enabled && prev.global.mode ? { mode: prev.global.mode } : {}),
					...(prev.global.enabled && prev.global.columns ? { columns: prev.global.columns } : {})
			  }
			: { enabled: false }
})

const setHasRowId = (set: Set<RowKey> | undefined, rowId: string): boolean => {
	if (!set || set.size === 0) return false
	if (set.has(rowId)) return true
	let found = false
	set.forEach((value) => {
		if (!found && String(value) === rowId) found = true
	})
	return found
}

const toComparable = (value: unknown): unknown => {
	if (value instanceof Date) return value.getTime()
	return value
}

const toArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : [])

const toLowerString = (value: unknown): string => String(value).toLowerCase()

const isNil = (value: unknown): boolean => value === null || value === undefined

const evaluateFilterOperator = (operator: FilterOperator, rowValue: unknown, clauseValue: unknown): boolean => {
	const left = toComparable(rowValue)
	const right = toComparable(clauseValue)

	switch (operator) {
		case 'eq':
			return left === right
		case 'neq':
			return left !== right
		case 'lt':
			return !isNil(left) && !isNil(right) && (left as number | string) < (right as number | string)
		case 'lte':
			return !isNil(left) && !isNil(right) && (left as number | string) <= (right as number | string)
		case 'gt':
			return !isNil(left) && !isNil(right) && (left as number | string) > (right as number | string)
		case 'gte':
			return !isNil(left) && !isNil(right) && (left as number | string) >= (right as number | string)
		case 'between': {
			const [min, max] = toArray(clauseValue).map(toComparable)
			if (isNil(left) || isNil(min) || isNil(max)) return false
			return (left as number | string) >= (min as number | string) && (left as number | string) <= (max as number | string)
		}
		case 'in': {
			const values = toArray(clauseValue)
			if (!values.length) return false
			return values.some((item) => toComparable(item) === left)
		}
		case 'notIn': {
			const values = toArray(clauseValue)
			if (!values.length) return true
			return values.every((item) => toComparable(item) !== left)
		}
		case 'contains':
			return !isNil(left) && !isNil(right) && toLowerString(left).includes(toLowerString(right))
		case 'notContains':
			return isNil(left) || isNil(right) || !toLowerString(left).includes(toLowerString(right))
		case 'startsWith':
			return !isNil(left) && !isNil(right) && toLowerString(left).startsWith(toLowerString(right))
		case 'endsWith':
			return !isNil(left) && !isNil(right) && toLowerString(left).endsWith(toLowerString(right))
		case 'isNull':
			return isNil(left)
		case 'isNotNull':
			return !isNil(left)
		case 'isEmpty':
			return left === '' || (Array.isArray(left) && left.length === 0)
		case 'isNotEmpty':
			return left !== '' && (!Array.isArray(left) || left.length > 0)
		case 'matches':
			if (isNil(left) || isNil(right)) return false
			try {
				return new RegExp(String(right), 'i').test(String(left))
			} catch {
				return false
			}
		default:
			return false
	}
}

const evaluateColumnClause = <T,>(row: T, clause: ColumnFilterClause, columnsById: Map<ColumnId, ColumnDef<T>>): boolean => {
	const column = columnsById.get(clause.id)
	if (!column) return false
	const rowValue = getValue(row, column)
	return evaluateFilterOperator(clause.op, rowValue, clause.value)
}

const isFilterGroup = (value: ColumnFilterClause | ColumnFilterGroup): value is ColumnFilterGroup =>
	Boolean(value && typeof value === 'object' && 'clauses' in value && 'op' in value)

const evaluateColumnFilterGroup = <T,>(row: T, group: ColumnFilterGroup, columnsById: Map<ColumnId, ColumnDef<T>>): boolean => {
	if (!group.clauses.length) return true
	if (group.op === 'or') {
		return group.clauses.some((clause) =>
			isFilterGroup(clause) ? evaluateColumnFilterGroup(row, clause, columnsById) : evaluateColumnClause(row, clause, columnsById)
		)
	}
	return group.clauses.every((clause) =>
		isFilterGroup(clause) ? evaluateColumnFilterGroup(row, clause, columnsById) : evaluateColumnClause(row, clause, columnsById)
	)
}

const isClientRows = <T,>(data: DataSource<T>): data is Extract<DataSource<T>, { mode: 'client' }> => data.mode === 'client'
const isServerRows = <T,>(data: DataSource<T>): data is Extract<DataSource<T>, { mode: 'server' }> => data.mode === 'server'

const columnId = <T,>(col: ColumnDef<T>): ColumnId => col.id

const getValue = <T,>(
	row: T,
	col: ColumnDef<T>,
	ctx?: { rowIndex: number; rowId: RowKey; columnsById?: Map<ColumnId, ColumnDef<T>> }
): unknown => {
	const acc = col.accessor
	if (typeof acc === 'function') {
		const rowIndex = ctx?.rowIndex ?? -1
		const rowId = ctx?.rowId ?? '0'
		return acc(row, {
			rowIndex,
			rowId,
			getValue: <V = unknown,>(columnId: ColumnId): V => {
				const linkedColumn = ctx?.columnsById?.get(columnId)
				if (!linkedColumn) return undefined as V
				return getValue(row, linkedColumn, ctx) as V
			}
		})
	}
	if (typeof acc === 'string' && acc) {
		const record = row as unknown as Record<string, unknown>
		return record[acc]
	}
	const record = row as unknown as Record<string, unknown>
	return record[col.id]
}

const isColumnSortable = <T,>(sortMode: SortMode, col: ColumnDef<T>): boolean => sortMode !== 'none' && col.sortable !== false
const isColumnResizable = <T,>(resizeMode: 'none' | 'onChange' | 'onEnd', col: ColumnDef<T>): boolean => resizeMode !== 'none' && col.resizable !== false

const toPixelWidth = (value: unknown): number | undefined => {
	if (typeof value === 'number' && Number.isFinite(value)) return value
	if (typeof value !== 'string') return undefined
	const trimmed = value.trim()
	if (!trimmed) return undefined
	if (trimmed.endsWith('px')) {
		const parsed = Number.parseFloat(trimmed.slice(0, -2))
		return Number.isFinite(parsed) ? parsed : undefined
	}
	return undefined
}

const toggleSort = (current: SortState, colId: ColumnId): SortState => {
	const existing = current.find((s) => s.id === colId)
	if (!existing) return [{ id: colId, dir: 'asc' }]
	if (existing.dir === 'asc') return [{ id: colId, dir: 'desc' }]
	return [{ id: colId, dir: 'asc' }]
}

const serializeExpansionForQueryKey = (query: TableQuery): { allExpanded: boolean; expanded: string[] } => ({
	allExpanded: Boolean(query.expansion.allExpanded),
	expanded: Array.from(query.expansion.expanded ?? new Set<RowKey>()).map((x) => String(x)).sort()
})

const makeServerQueryKey = (query: TableQuery): string =>
	JSON.stringify({
		sort: query.sort,
		filter: query.filter,
		pagination: query.pagination,
		grouping: query.grouping,
		expansion: serializeExpansionForQueryKey(query),
		columnOrder: query.columnOrder,
		columnVisibility: query.columnVisibility
	})

export const DataTable = <T,>(props: DataTableProps<T>): React.ReactElement => {
	const {
		title,
		columns,
		data,
		className,
		style,
		id,
		layout,
		features,
		slots,
		initialState,
		state,
		onStateChange,
		onSortChange,
		onFilterChange,
		onPaginationChange,
		onSelectionChange,
		onColumnOrderChange,
		onColumnPinningChange,
		onColumnSizingChange,
		onColumnVisibilityChange,
		onDensityChange,
		onRowClick,
		onEvent,
		onTableReady
	} = props
	const searchDebounceMs = Math.max(0, Number(props.searchDebounceMs ?? 0))

	const initialGlobalSearchQuery = props.initialGlobalSearchQuery
	const initialPagination = props.pagination
	const sortMode: SortMode = props.sortMode ?? 'none'
	const filterMode: FilterMode = props.filterMode ?? 'none'
	const paginationMode: PaginationMode = props.paginationMode ?? 'none'
	const columnResizeMode = features?.columnResize ?? 'none'
	const isGlobalSearchEnabled = filterMode !== 'none' && features?.globalSearch !== false
	const normalizedInitialFilter =
		initialGlobalSearchQuery !== undefined
			? setGlobalQuery(initialState?.filter ?? defaultFilterState, initialGlobalSearchQuery)
			: initialState?.filter
	const bootstrapState: Partial<TableState> = {
		...initialState,
		...(initialPagination ? { pagination: initialPagination } : {}),
		...(normalizedInitialFilter ? { filter: normalizedInitialFilter } : {})
	}

	// ----- state (controlled/uncontrolled) -----
	const [internalState, setInternalState] = React.useState<TableState>(() => ({
		...defaultTableState,
		...bootstrapState
	}))

	const effectiveState: TableState = {
		...internalState,
		...state
	} as TableState

	const emitSliceCallbacks = React.useCallback(
		(next: TableState, slice: StateChangeMeta['slice']): void => {
			switch (slice) {
				case 'sort':
					onSortChange?.(next.sort)
					onEvent?.({ type: 'sortChange', sort: next.sort })
					break
				case 'filter':
					onFilterChange?.(next.filter)
					onEvent?.({ type: 'filterChange', filter: next.filter })
					break
				case 'pagination':
					onPaginationChange?.(next.pagination)
					onEvent?.({ type: 'pageChange', pagination: next.pagination })
					break
				case 'selection':
					onSelectionChange?.(next.selection)
					onEvent?.({ type: 'selectionChange', selection: next.selection })
					break
				case 'columnOrder':
					onColumnOrderChange?.(next.columnOrder)
					onEvent?.({ type: 'columnReorder', columnOrder: next.columnOrder })
					break
				case 'columnPinning':
					onColumnPinningChange?.(next.columnPinning)
					break
				case 'columnSizing':
					onColumnSizingChange?.(next.columnSizing)
					break
				case 'columnVisibility':
					onColumnVisibilityChange?.(next.columnVisibility)
					onEvent?.({ type: 'columnVisibilityChange', columnVisibility: next.columnVisibility })
					break
				case 'density':
					onDensityChange?.(next.density)
					onEvent?.({ type: 'densityChange', density: next.density })
					break
				default:
					break
			}
		},
		[
			onSortChange,
			onFilterChange,
			onPaginationChange,
			onSelectionChange,
			onColumnOrderChange,
			onColumnPinningChange,
			onColumnSizingChange,
			onColumnVisibilityChange,
			onDensityChange,
			onEvent
		]
	)

	const commitState = (next: TableState, meta: Pick<StateChangeMeta, 'slice' | 'reason'>): void => {
		const normalizedNext = normalizeNextStateForSlice(next, meta.slice)
		if (isSameSliceState(effectiveState, normalizedNext, meta.slice)) return
		onStateChange?.(normalizedNext, meta)
		emitSliceCallbacks(normalizedNext, meta.slice)
		setInternalState(normalizedNext)
	}
	const commitGlobalSearchQueryRef = React.useRef<(query: string) => void>(() => undefined)
	commitGlobalSearchQueryRef.current = (query: string): void => {
		commitState({ ...effectiveState, filter: setGlobalQuery(effectiveState.filter, query) }, { slice: 'filter' })
	}
	const searchDebounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
	const [searchDebouncePending, setSearchDebouncePending] = React.useState(false)
	const [globalSearchInputQuery, setGlobalSearchInputQuery] = React.useState<string>('')
	React.useEffect(() => {
		if (searchDebouncePending) return
		const committedQuery = getGlobalQuery(effectiveState.filter)
		setGlobalSearchInputQuery((prev) => (prev === committedQuery ? prev : committedQuery))
	}, [effectiveState.filter, searchDebouncePending])
	React.useEffect(
		() => () => {
			if (searchDebounceTimerRef.current) clearTimeout(searchDebounceTimerRef.current)
		},
		[]
	)
	const setGlobalSearchQuery = React.useCallback(
		(query: string): void => {
			setGlobalSearchInputQuery(query)
			if (searchDebounceTimerRef.current) {
				clearTimeout(searchDebounceTimerRef.current)
				searchDebounceTimerRef.current = null
			}
			if (searchDebounceMs <= 0) {
				setSearchDebouncePending(false)
				commitGlobalSearchQueryRef.current(query)
				return
			}
			setSearchDebouncePending(true)
			searchDebounceTimerRef.current = setTimeout(() => {
				searchDebounceTimerRef.current = null
				commitGlobalSearchQueryRef.current(query)
				setSearchDebouncePending(false)
			}, searchDebounceMs)
		},
		[searchDebounceMs]
	)

	type ActiveResize = {
		columnId: ColumnId
		startX: number
		startWidth: number
		minWidth: number
		maxWidth?: number
	}
	const [activeResize, setActiveResize] = React.useState<ActiveResize | null>(null)
	const activeResizeRef = React.useRef<ActiveResize | null>(null)
	activeResizeRef.current = activeResize

	const resizeModeRef = React.useRef(columnResizeMode)
	resizeModeRef.current = columnResizeMode

	const commitColumnWidth = React.useCallback(
		(columnId: ColumnId, width: number): void => {
			const current = effectiveState.columnSizing[columnId]
			if (current?.width === width) return
			commitState(
				{
					...effectiveState,
					columnSizing: {
						...effectiveState.columnSizing,
						[columnId]: {
							...current,
							width
						}
					}
				},
				{ slice: 'columnSizing', reason: 'column-resize' }
			)
		},
		[effectiveState]
	)
	const commitColumnWidthRef = React.useRef(commitColumnWidth)
	commitColumnWidthRef.current = commitColumnWidth

	const resetColumnWidth = React.useCallback(
		(columnId: ColumnId, width?: number): void => {
			const current = effectiveState.columnSizing[columnId]
			if (current?.width === width) return

			// If there is no explicit width to restore and no other runtime sizing, remove the override entry.
			if (width === undefined && current && current.minWidth === undefined && current.maxWidth === undefined) {
				const nextSizing = { ...effectiveState.columnSizing }
				delete nextSizing[columnId]
				commitState(
					{
						...effectiveState,
						columnSizing: nextSizing
					},
					{ slice: 'columnSizing', reason: 'column-resize-reset' }
				)
				return
			}

			commitState(
				{
					...effectiveState,
					columnSizing: {
						...effectiveState.columnSizing,
						[columnId]: {
							...current,
							width
						}
					}
				},
				{ slice: 'columnSizing', reason: 'column-resize-reset' }
			)
		},
		[effectiveState]
	)

	React.useEffect(() => {
		if (!activeResize) return
		const onMouseMove = (event: MouseEvent): void => {
			const current = activeResizeRef.current
			if (!current) return
			const delta = event.clientX - current.startX
			const rawWidth = current.startWidth + delta
			const withMin = Math.max(rawWidth, current.minWidth)
			const clamped = current.maxWidth !== undefined ? Math.min(withMin, current.maxWidth) : withMin
			if (resizeModeRef.current === 'onChange') {
				commitColumnWidthRef.current(current.columnId, Math.round(clamped))
			}
		}
		const onMouseUp = (event: MouseEvent): void => {
			const current = activeResizeRef.current
			if (!current) return
			const delta = event.clientX - current.startX
			const rawWidth = current.startWidth + delta
			const withMin = Math.max(rawWidth, current.minWidth)
			const clamped = current.maxWidth !== undefined ? Math.min(withMin, current.maxWidth) : withMin
			commitColumnWidthRef.current(current.columnId, Math.round(clamped))
			setActiveResize(null)
		}
		window.addEventListener('mousemove', onMouseMove)
		window.addEventListener('mouseup', onMouseUp)
		return () => {
			window.removeEventListener('mousemove', onMouseMove)
			window.removeEventListener('mouseup', onMouseUp)
		}
	}, [activeResize])

	// ----- server fetching -----
	const [serverRows, setServerRows] = React.useState<T[] | null>(null)
	const [serverPageInfo, setServerPageInfo] = React.useState<TableQueryResult<T>['pageInfo'] | null>(null)
	const [serverError, setServerError] = React.useState<unknown>(null)
	const [serverLoading, setServerLoading] = React.useState(false)

	const abortRef = React.useRef<AbortController | null>(null)
	const isServer = isServerRows(data)
	const serverFetchRef = React.useRef<null | ((query: TableQuery, ctx: FetchContext) => Promise<TableQueryResult<T>> | TableQueryResult<T>)>(null)
	serverFetchRef.current = isServer ? data.fetch : null
	const lastCompletedServerQueryKeyRef = React.useRef<string | null>(null)
	const inFlightServerQueryKeyRef = React.useRef<string | null>(null)

	const refetch = React.useCallback(
		async (reason?: string, skipDedupe = false) => {
			const fetchFn = serverFetchRef.current
			if (!fetchFn) return
			const query: TableQuery = {
				sort: effectiveState.sort,
				filter: effectiveState.filter,
				pagination: effectiveState.pagination,
				grouping: effectiveState.grouping,
				expansion: effectiveState.expansion,
				columnOrder: effectiveState.columnOrder.length ? effectiveState.columnOrder : undefined,
				columnVisibility: Object.keys(effectiveState.columnVisibility).length ? effectiveState.columnVisibility : undefined
			}
			const queryKey = makeServerQueryKey(query)

			// Avoid re-fetching identical queries by default.
			// - If a query is already in flight, do not duplicate it.
			// - If a query completed successfully, do not re-run it automatically.
			if (!skipDedupe && inFlightServerQueryKeyRef.current === queryKey) {
				// Allow immediate retry when the previous in-flight request for this query
				// has already been aborted (e.g. mount/effect churn in development mode).
				if (!abortRef.current?.signal.aborted) {
					return
				}
			}
			if (!skipDedupe && lastCompletedServerQueryKeyRef.current === queryKey) {
				return
			}

			abortRef.current?.abort()
			const ac = new AbortController()
			abortRef.current = ac

			inFlightServerQueryKeyRef.current = queryKey

			const ctx: FetchContext = { signal: ac.signal, requestId: reason }

			setServerLoading(true)
			setServerError(null)
			try {
				const result = await fetchFn(query, ctx)
				if (ac.signal.aborted) return
				setServerRows(result.rows)
				setServerPageInfo(result.pageInfo)
				lastCompletedServerQueryKeyRef.current = queryKey
			} catch (err) {
				if (ac.signal.aborted) return
				setServerError(err)
			} finally {
				// Only the latest request should end loading / clear in-flight.
				if (inFlightServerQueryKeyRef.current === queryKey && !ac.signal.aborted) {
					inFlightServerQueryKeyRef.current = null
					setServerLoading(false)
				}
				// If we aborted due to unmount (e.g. React strict mode), still clear loading/in-flight.
				if (inFlightServerQueryKeyRef.current === queryKey && ac.signal.aborted) {
					inFlightServerQueryKeyRef.current = null
					setServerLoading(false)
				}
			}
		},
		[
			effectiveState.sort,
			effectiveState.filter,
			effectiveState.pagination,
			effectiveState.grouping,
			effectiveState.expansion,
			effectiveState.columnOrder,
			effectiveState.columnVisibility
		]
	)

	React.useEffect(() => {
		if (!isServer) return
		void refetch('state-change')
		return () => {
			abortRef.current?.abort()
		}
	}, [isServer, refetch])

	const rows: T[] = React.useMemo(() => {
		if (isClientRows(data)) return data.rows
		return serverRows ?? []
	}, [data, serverRows])

	const columnsById = React.useMemo(() => new Map<ColumnId, ColumnDef<T>>(columns.map((c) => [c.id, c])), [columns])

	const getRowId = React.useCallback(
		(row: T, rowIndex: number): string => (props.getRowId ? props.getRowId(row, rowIndex) : String(rowIndex)),
		[props.getRowId]
	)

	// ----- filtering (client) -----
	const filterQuery = filterMode === 'none' ? '' : getGlobalQuery(effectiveState.filter)

	const indexedData = React.useMemo<Array<IndexedRow<T>>>(() => rows.map((original, baseIndex) => ({ original, baseIndex })), [rows])

	const clientFilteredData = React.useMemo((): Array<IndexedRow<T>> => {
		if (filterMode !== 'client') return indexedData
		const globalQuery = filterQuery.trim().toLowerCase()
		const globalColumns = effectiveState.filter.global.enabled ? effectiveState.filter.global.columns : undefined
		const globalColumnsSet = globalColumns && globalColumns.length > 0 ? new Set(globalColumns) : null
		const hasGlobalFilter = globalQuery.length > 0
		const hasColumnFilter = effectiveState.filter.column.clauses.length > 0
		if (!hasGlobalFilter && !hasColumnFilter) return indexedData

		return indexedData.filter((row) => {
			const globalMatched = !hasGlobalFilter
				? true
				: columns.some((col) => {
						if (col.globalSearch === false) return false
						if (globalColumnsSet && !globalColumnsSet.has(col.id)) return false
						const rowId = getRowId(row.original, row.baseIndex)
						const value = getValue(row.original, col, { rowIndex: row.baseIndex, rowId, columnsById })
						if (value === null || value === undefined) return false
						if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
							return String(value).toLowerCase().includes(globalQuery)
						}
						return false
				  })
			if (!globalMatched) return false
			if (!hasColumnFilter) return true
			return evaluateColumnFilterGroup(row.original, effectiveState.filter.column, columnsById)
		})
	}, [indexedData, columns, filterMode, filterQuery, effectiveState.filter, columnsById, getRowId])

	// ----- sorting (client) -----
	const clientSortedData = React.useMemo(() => {
		if (sortMode !== 'client') return clientFilteredData
		const first = effectiveState.sort[0]
		if (!first) return clientFilteredData
		const col = columns.find((c) => c.id === first.id)
		if (!col || col.sortable === false) return clientFilteredData
		const dir = first.dir === 'asc' ? 1 : -1
		const copy = [...clientFilteredData]
		copy.sort((aRow, bRow) => {
			const a = aRow.original
			const b = bRow.original
			const va = getValue(a, col, { rowIndex: aRow.baseIndex, rowId: getRowId(a, aRow.baseIndex), columnsById })
			const vb = getValue(b, col, { rowIndex: bRow.baseIndex, rowId: getRowId(b, bRow.baseIndex), columnsById })
			if (va === vb) return 0
			if (va === null || va === undefined) return 1
			if (vb === null || vb === undefined) return -1
			if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
			if (typeof va === 'boolean' && typeof vb === 'boolean') return (Number(va) - Number(vb)) * dir
			return String(va).localeCompare(String(vb), undefined, { sensitivity: 'base' }) * dir
		})
		return copy
	}, [clientFilteredData, columns, effectiveState.sort, sortMode, columnsById, getRowId])

	// ----- pagination (client) -----
	const clientPaginatedData = React.useMemo(() => {
		if (paginationMode !== 'client') return clientSortedData
		if (effectiveState.pagination.mode !== 'client') return clientSortedData
		const start = effectiveState.pagination.pageIndex * effectiveState.pagination.pageSize
		const end = start + effectiveState.pagination.pageSize
		return clientSortedData.slice(start, end)
	}, [clientSortedData, effectiveState.pagination, paginationMode])

	const visibleIndexed = paginationMode === 'client' ? clientPaginatedData : clientSortedData
	const visibleRows = visibleIndexed.map((r) => r.original)
	const resolveVisibleRowIndex = React.useCallback(
		(row: IndexedRow<T>): number => {
			if (paginationMode === 'server' && effectiveState.pagination.mode === 'server') {
				const pageIndex = effectiveState.pagination.pageIndex ?? 0
				return pageIndex * effectiveState.pagination.pageSize + row.baseIndex
			}
			return row.baseIndex
		},
		[paginationMode, effectiveState.pagination]
	)

	// ----- rowCount for pagination UI -----
	const rowCount = React.useMemo(() => {
		if (paginationMode === 'server') {
			if (serverPageInfo?.kind === 'offset') return serverPageInfo.totalRows
			if (serverPageInfo?.kind === 'cursor') return serverPageInfo.approxTotalRows ?? visibleRows.length
			return visibleRows.length
		}
		return clientSortedData.length
	}, [paginationMode, serverPageInfo, visibleRows.length, clientSortedData.length])

	// ----- scroll/maxHeight -----
	const maxHeight = layout?.scroll?.maxHeight
	const scrollX = layout?.scroll?.x
	const scrollY = layout?.scroll?.y
	const overscroll = layout?.scroll?.overscroll
	const paginationState = effectiveState.pagination
	const isPaginationEnabled = paginationMode !== 'none' && paginationState.mode !== 'none'
	const stickySpec = layout?.sticky ?? features?.sticky
	const isHeaderSticky = Boolean(stickySpec?.header)
	const isFooterSticky = Boolean(stickySpec?.footer)
	const areColumnsSticky = Boolean(stickySpec?.columns)

	// ----- CSV/print -----
	const enableCsv = Boolean(features?.export?.csv)
	const enablePrint = Boolean(features?.export?.print)
	const customToolbarMenuItems = props.toolbarMenuItems ?? []
	const enableToolbarMenu = enableCsv || enablePrint || customToolbarMenuItems.length > 0
	const isServerBusy = isServerRows(data) && (serverLoading || searchDebouncePending)

	const printRef = React.useRef<HTMLDivElement>(null)

	const onDownloadCsv = (): void => {
		const fileName = id ? `${id}.csv` : 'table.csv'
		const csvHeaders = columns.map((c) => (typeof c.header === 'string' ? c.header : c.id))
		const csvItems = (sortMode === 'client' ? clientSortedData.map((r) => r.original) : visibleRows).map((row) =>
			columns.map((c) => {
				const v = getValue(row, c)
				return v === null || v === undefined ? '' : String(v)
			})
		)
		downloadCsv(csvItems, csvHeaders, fileName)
	}

	// ----- render helpers -----
	const colStyles = React.useMemo(() => {
		type SizeLike = {
			width?: unknown
			minWidth?: unknown
			maxWidth?: unknown
			grow?: unknown
		}

		const asSizeLike = (value: unknown): SizeLike | null => {
			if (!value || typeof value !== 'object') return null
			return value as SizeLike
		}

		const growCols = columns
			.map((c) => {
				const size = asSizeLike(c.size)
				const grow = typeof size?.grow === 'number' ? size.grow : 0
				return { c, grow }
			})
			.filter((x) => x.grow > 0)

		const hasFlexGrow = growCols.length > 0
		const totalGrow = growCols.reduce((acc, x) => acc + x.grow, 0)

		const styles: React.CSSProperties[] = []
		for (const c of columns) {
			const s: React.CSSProperties = {}
			const size = asSizeLike(c.size)
			const runtimeSizing = effectiveState.columnSizing[c.id]
			if (size) {
				const toCss = (v: unknown): string | number | undefined => (typeof v === 'number' || typeof v === 'string' ? v : undefined)
				if (size.minWidth !== undefined) s.minWidth = toCss(size.minWidth)
				if (size.maxWidth !== undefined) s.maxWidth = toCss(size.maxWidth)
				if (size.width !== undefined) s.width = toCss(size.width)
			}
			if (runtimeSizing?.minWidth !== undefined) s.minWidth = runtimeSizing.minWidth
			if (runtimeSizing?.maxWidth !== undefined) s.maxWidth = runtimeSizing.maxWidth
			if (runtimeSizing?.width !== undefined) s.width = runtimeSizing.width

			if (hasFlexGrow && typeof size?.grow === 'number' && size.grow > 0 && s.width === undefined) {
				// When multiple columns are "grow", distribute width proportionally.
				if (growCols.length === 1) {
					s.width = '100%'
				} else if (totalGrow > 0) {
					s.width = `${(size.grow / totalGrow) * 100}%`
				}
			}

			styles.push(s)
		}
		return styles
	}, [columns, effectiveState.columnSizing])
	const leftPinnedColumns = React.useMemo(
		() => (areColumnsSticky ? new Set<ColumnId>(effectiveState.columnPinning.left) : new Set<ColumnId>()),
		[areColumnsSticky, effectiveState.columnPinning.left]
	)
	const rightPinnedColumns = React.useMemo(
		() => (areColumnsSticky ? new Set<ColumnId>(effectiveState.columnPinning.right) : new Set<ColumnId>()),
		[areColumnsSticky, effectiveState.columnPinning.right]
	)
	const columnPixelWidths = React.useMemo(
		() =>
			columns.map((col, index) => {
				const runtimeWidth = effectiveState.columnSizing[col.id]?.width
				if (typeof runtimeWidth === 'number' && Number.isFinite(runtimeWidth)) return runtimeWidth
				const style = colStyles[index]
				return toPixelWidth(style?.width) ?? toPixelWidth(style?.minWidth) ?? 0
			}),
		[columns, colStyles, effectiveState.columnSizing]
	)
	const pinnedOffsets = React.useMemo(() => {
		const left = new Map<ColumnId, number>()
		const right = new Map<ColumnId, number>()
		if (!areColumnsSticky) return { left, right }

		let leftOffset = 0
		for (let i = 0; i < columns.length; i += 1) {
			const col = columns[i]
			if (!leftPinnedColumns.has(col.id)) continue
			left.set(col.id, leftOffset)
			leftOffset += columnPixelWidths[i]
		}

		let rightOffset = 0
		for (let i = columns.length - 1; i >= 0; i -= 1) {
			const col = columns[i]
			if (!rightPinnedColumns.has(col.id)) continue
			right.set(col.id, rightOffset)
			rightOffset += columnPixelWidths[i]
		}
		return { left, right }
	}, [areColumnsSticky, columns, leftPinnedColumns, rightPinnedColumns, columnPixelWidths])
	const getPinnedStyles = React.useCallback(
		(colId: ColumnId, isHeaderCell: boolean): React.CSSProperties | undefined => {
			if (!areColumnsSticky) return undefined
			const leftOffset = pinnedOffsets.left.get(colId)
			if (leftOffset !== undefined) {
				return {
					position: 'sticky',
					left: leftOffset,
					zIndex: isHeaderCell ? 4 : 2,
					background: 'inherit',
					boxShadow: '1px 0 0 rgba(255, 255, 255, 0.08)'
				}
			}
			const rightOffset = pinnedOffsets.right.get(colId)
			if (rightOffset !== undefined) {
				return {
					position: 'sticky',
					right: rightOffset,
					zIndex: isHeaderCell ? 4 : 2,
					background: 'inherit',
					boxShadow: '-1px 0 0 rgba(255, 255, 255, 0.08)'
				}
			}
			return undefined
		},
		[areColumnsSticky, pinnedOffsets]
	)

	const isRowSelected = React.useCallback(
		(rowId: string): boolean => {
			const selection = effectiveState.selection
			if (selection.mode === 'none') return false
			if (selection.mode === 'single') {
				return selection.selectedRowId !== null && String(selection.selectedRowId) === rowId
			}
			if (selection.allSelected) {
				return !setHasRowId(selection.excludedRowIds, rowId)
			}
			return setHasRowId(selection.selectedRowIds, rowId)
		},
		[effectiveState.selection]
	)

	const isRowExpanded = React.useCallback(
		(rowId: string): boolean => {
			const expansion = effectiveState.expansion
			if (expansion.allExpanded) return true
			return setHasRowId(expansion.expanded, rowId)
		},
		[effectiveState.expansion]
	)

	const makeColumnRuntime = React.useCallback(
		(col: ColumnDef<T>): ColumnRuntime<T> => {
			const size = effectiveState.columnSizing[col.id]
			const pinning = effectiveState.columnPinning
			return {
				id: col.id,
				def: col,
				getSize: (): { width?: number; minWidth?: number; maxWidth?: number } => ({
					width: size?.width,
					minWidth: size?.minWidth,
					maxWidth: size?.maxWidth
				}),
				isVisible: (): boolean => effectiveState.columnVisibility[col.id] !== 'hidden',
				isPinned: (): 'left' | 'right' | null => {
					if (pinning.left.includes(col.id)) return 'left'
					if (pinning.right.includes(col.id)) return 'right'
					return null
				}
			}
		},
		[effectiveState.columnSizing, effectiveState.columnPinning, effectiveState.columnVisibility]
	)

	const makeRowRuntime = React.useCallback(
		(row: T, rowIndex: number): RowRuntime<T> => {
			const rowId = getRowId(row, rowIndex)
			return {
				id: rowId,
				index: rowIndex,
				original: row,
				getValue: <V = unknown,>(cid: ColumnId): V => {
					const c = columnsById.get(cid)
					return c ? (getValue(row, c, { rowIndex, rowId, columnsById }) as V) : (undefined as V)
				},
				isSelected: (): boolean => isRowSelected(rowId),
				isExpanded: (): boolean => isRowExpanded(rowId)
			}
		},
		[getRowId, columnsById, isRowSelected, isRowExpanded]
	)

	const tableRuntimeForSlots = React.useMemo<TableRuntime<T>>(
		() => ({
			getState: (): TableState => effectiveState,
			getGlobalSearchQuery: (): string => globalSearchInputQuery,
			setSort: (next: SortState): void => {
				commitState({ ...effectiveState, sort: next }, { slice: 'sort' })
			},
			setFilter: (next: FilterState): void => {
				commitState({ ...effectiveState, filter: next }, { slice: 'filter' })
			},
			setGlobalSearchQuery,
			setPagination: (next: TableState['pagination']): void => {
				commitState({ ...effectiveState, pagination: next }, { slice: 'pagination' })
			},
			setSelection: (next: TableState['selection']): void => {
				commitState({ ...effectiveState, selection: next }, { slice: 'selection' })
			},
			setColumnOrder: (next: TableState['columnOrder']): void => {
				commitState({ ...effectiveState, columnOrder: next }, { slice: 'columnOrder' })
			},
			setColumnPinning: (next: TableState['columnPinning']): void => {
				commitState({ ...effectiveState, columnPinning: next }, { slice: 'columnPinning' })
			},
			setColumnSizing: (next: TableState['columnSizing']): void => {
				commitState({ ...effectiveState, columnSizing: next }, { slice: 'columnSizing' })
			},
			setColumnVisibility: (next: TableState['columnVisibility']): void => {
				commitState({ ...effectiveState, columnVisibility: next }, { slice: 'columnVisibility' })
			},
			setDensity: (next: TableState['density']): void => {
				commitState({ ...effectiveState, density: next }, { slice: 'density' })
			},
			getRows: (): RowRuntime<T>[] => visibleIndexed.map((row) => makeRowRuntime(row.original, resolveVisibleRowIndex(row))),
			getColumns: (): ColumnRuntime<T>[] => columns.map((col) => makeColumnRuntime(col)),
			refetch: (reason?: string) => void refetch(reason ?? 'manual', true)
		}),
		[effectiveState, globalSearchInputQuery, setGlobalSearchQuery, visibleIndexed, resolveVisibleRowIndex, makeRowRuntime, columns, makeColumnRuntime, refetch]
	)
	const tableRuntimeForSlotsRef = React.useRef<TableRuntime<T>>(tableRuntimeForSlots)
	tableRuntimeForSlotsRef.current = tableRuntimeForSlots
	const tableRuntimeForReady = React.useMemo<TableRuntime<T>>(
		() => ({
			getState: () => tableRuntimeForSlotsRef.current.getState(),
			getGlobalSearchQuery: () => tableRuntimeForSlotsRef.current.getGlobalSearchQuery(),
			setSort: (next) => tableRuntimeForSlotsRef.current.setSort(next),
			setFilter: (next) => tableRuntimeForSlotsRef.current.setFilter(next),
			setGlobalSearchQuery: (next) => tableRuntimeForSlotsRef.current.setGlobalSearchQuery(next),
			setPagination: (next) => tableRuntimeForSlotsRef.current.setPagination(next),
			setSelection: (next) => tableRuntimeForSlotsRef.current.setSelection(next),
			setColumnOrder: (next) => tableRuntimeForSlotsRef.current.setColumnOrder(next),
			setColumnPinning: (next) => tableRuntimeForSlotsRef.current.setColumnPinning(next),
			setColumnSizing: (next) => tableRuntimeForSlotsRef.current.setColumnSizing(next),
			setColumnVisibility: (next) => tableRuntimeForSlotsRef.current.setColumnVisibility(next),
			setDensity: (next) => tableRuntimeForSlotsRef.current.setDensity(next),
			getRows: () => tableRuntimeForSlotsRef.current.getRows(),
			getColumns: () => tableRuntimeForSlotsRef.current.getColumns(),
			refetch: (reason?: string) => tableRuntimeForSlotsRef.current.refetch?.(reason)
		}),
		[]
	)
	React.useEffect(() => {
		onTableReady?.(tableRuntimeForReady)
	}, [onTableReady, tableRuntimeForReady])

	const renderHeader = (col: ColumnDef<T>): React.ReactNode => {
		if (typeof col.header === 'function') {
			const runtime = makeColumnRuntime(col)
			return col.header({ column: runtime, table: tableRuntimeForSlots })
		}
		return col.header
	}

	const renderCell = (row: T, rowIndex: number, col: ColumnDef<T>): React.ReactNode => {
		const rowId = getRowId(row, rowIndex)
		const value = getValue(row, col, { rowIndex, rowId, columnsById })
		if (col.cell) {
			const rowRuntime = makeRowRuntime(row, rowIndex)
			const colRuntime = makeColumnRuntime(col)
			return col.cell({
				row: rowRuntime,
				column: colRuntime,
				table: tableRuntimeForSlots,
				value
			})
		}
		const text = value === null || value === undefined ? '' : String(value)
		return <HighlightedText text={text} highlight={filterMode === 'none' ? '' : filterQuery} />
	}

	const handleRowClick = (row: T, rowIndex: number): void => {
		const rid = getRowId(row, rowIndex)
		onRowClick?.(row, rid, rowIndex)
		if (!onEvent) return
		onEvent({ type: 'rowClick', rowId: rid })
	}

	const rootClasses = [css.root, className].filter(Boolean).join(' ')
	const titleNode = title ? typeof title === 'string' ? <h1 className={css.title}>{title}</h1> : title : null
	const isRowClickEnabled = Boolean(onRowClick || onEvent)

	const toolbarNode = slots?.toolbar
		? slots.toolbar({ table: tableRuntimeForSlots })
		: (isGlobalSearchEnabled || enableCsv || isServerRows(data)) && (
				<div className={toolbarCss.toolbarContainer}>
					{isGlobalSearchEnabled && (
						<SearchContainer
							handleSearchChange={(value: string): void => {
								setGlobalSearchQuery(value)
							}}
							isSearchVisible
							searchText={globalSearchInputQuery}
						/>
					)}
					{isServerRows(data) && (
						<BasicButton
							isLoading={isServerBusy}
							onClick={async (): Promise<void> => {
								await refetch('manual', true)
							}}
							title={i18next.t('components.table.refresh', 'Refresh')}
							icon={RefreshIcon}
							className={toolbarCss.refreshBtn}
						></BasicButton>
					)}
					{enableToolbarMenu && (
						<Dropdown title={<MoreVertIcon className={toolbarCss.dropdownIcon} />} className={toolbarCss['dropdown']}>
							{enableCsv && (
								<li key="downloadCsv">
									<div onClick={onDownloadCsv} title={id ? `${id}.csv` : 'table.csv'}>
										<DownloadIcon />
										<div>{i18next.t('components.table.downloadCsv')}</div>
									</div>
								</li>
							)}
							{enablePrint && (
								<li key="print">
									<ReactToPrint
										trigger={(): React.ReactElement => (
											<div title={i18next.t('components.table.print')}>
												<PrintIcon />
												<div>{i18next.t('components.table.print')}</div>
											</div>
										)}
										content={(): HTMLDivElement | null => printRef.current}
									/>
								</li>
							)}
							{customToolbarMenuItems.map((node, i) => (
								<li key={`customToolbarMenuItem:${i}`}>{node}</li>
							))}
						</Dropdown>
					)}
				</div>
		  )
	const EmptyStateSlot = slots?.emptyState
	const LoadingStateSlot = slots?.loadingState
	const ErrorStateSlot = slots?.errorState
	const ContainerSlot = slots?.container
	const hasToolbarRow = Boolean(titleNode || toolbarNode)

	const tableContent = (
		<div className={rootClasses} style={style}>
			{hasToolbarRow && (
				<div className={css.toolbarRow}>
					{titleNode}
					{toolbarNode}
				</div>
			)}

			<div
				className={[css.scrollContainer, isPaginationEnabled ? css.scrollContainerWithPagination : ''].filter(Boolean).join(' ')}
				style={{ maxHeight, overflowX: scrollX, overflowY: scrollY, overscrollBehavior: overscroll }}
				ref={printRef}
			>
				<table className={css.table} role="table" aria-label={i18next.t('components.table.ariaLabel', 'Table')}>
					<thead className={[css.thead, isHeaderSticky ? css.theadSticky : ''].filter(Boolean).join(' ')}>
						<tr>
							{columns.map((col, i) => {
								const id = columnId(col)
								const sortable = isColumnSortable(sortMode, col)
								const resizable = isColumnResizable(columnResizeMode, col)
								const align = col.align || 'left'
								const alignClass = css[`align${align[0].toUpperCase()}${align.slice(1)}` as keyof typeof css]
								const thClassNames = [css.th, alignClass, resizable ? css.thResizable : ''].filter(Boolean).join(' ')
								const thButtonClassNames = [css.thButton, alignClass].filter(Boolean).join(' ')
								const thButtonDisabledClassNames = [css.thButton, css.thButtonDisabled, alignClass].filter(Boolean).join(' ')
								return (
									<th
										key={id}
										className={thClassNames}
										style={{ ...colStyles[i], ...getPinnedStyles(id, true) }}
										scope="col"
										aria-sort="none"
									>
										{sortable ? (
											<button
												type="button"
												className={thButtonClassNames}
												style={colStyles[i]}
												onClick={(): void => {
													commitState({ ...effectiveState, sort: toggleSort(effectiveState.sort, id) }, { slice: 'sort' })
												}}
											>
												<span>{renderHeader(col)}</span>
												{effectiveState.sort[0]?.id === id && (
													<PlayIcon
														className={css.sortIcon}
														style={{ transform: `rotate(${effectiveState.sort[0]?.dir === 'asc' ? 90 : -90}deg)` }}
													/>
												)}
											</button>
										) : (
											<span className={thButtonDisabledClassNames} style={colStyles[i]}>
												{renderHeader(col)}
											</span>
										)}
										{resizable && (
											<div
												className={css.resizeHandle}
												role="separator"
												aria-orientation="vertical"
												onDoubleClick={(event): void => {
													event.preventDefault()
													event.stopPropagation()
													setActiveResize(null)
													const sizeSpec = col.size as { width?: unknown } | undefined
													const defaultWidth = toPixelWidth(sizeSpec?.width)
													resetColumnWidth(col.id, defaultWidth)
												}}
												onMouseDown={(event): void => {
													event.preventDefault()
													event.stopPropagation()
													const header = event.currentTarget.closest('th') as HTMLTableCellElement | null
													const runtimeSizing = effectiveState.columnSizing[col.id]
													const startWidth = runtimeSizing?.width ?? (header ? header.getBoundingClientRect().width : 0)
													if (!startWidth) return
													const sizeSpec = col.size as { minWidth?: unknown; maxWidth?: unknown } | undefined
													const configuredMinWidth =
														runtimeSizing?.minWidth ?? toPixelWidth(sizeSpec?.minWidth) ?? defaultMinResizableWidthPx
													const minWidth = Math.max(defaultMinResizableWidthPx, configuredMinWidth)
													const maxWidth = runtimeSizing?.maxWidth ?? toPixelWidth(sizeSpec?.maxWidth)
													setActiveResize({
														columnId: col.id,
														startX: event.clientX,
														startWidth,
														minWidth,
														maxWidth
													})
												}}
											/>
										)}
									</th>
								)
							})}
						</tr>
					</thead>
					<tbody>
						{serverError && visibleRows.length === 0 ? (
							<tr>
								<td className={css.emptyState} colSpan={columns.length}>
									{ErrorStateSlot ? (
										<ErrorStateSlot error={serverError} table={tableRuntimeForSlots} />
									) : (
										<div className={`${css.stateCellInner} ${css.emptyStateInner}`}>
											{serverError instanceof Error ? serverError.message : String(serverError ?? 'Failed')}
										</div>
									)}
								</td>
							</tr>
						) : isServerBusy && visibleRows.length === 0 ? (
							<tr>
								<td className={css.loadingState} colSpan={columns.length}>
									{LoadingStateSlot ? (
										<LoadingStateSlot table={tableRuntimeForSlots} />
									) : (
										<div className={`${css.stateCellInner} ${css.emptyStateInner}`}>{i18next.t('components.table.loading', 'Loading...')}</div>
									)}
								</td>
							</tr>
						) : visibleRows.length ? (
							visibleIndexed.map((row) => {
								const rowIndex = resolveVisibleRowIndex(row)
								const rowId = getRowId(row.original, rowIndex)
								const isRowClickable = Boolean(onRowClick || onEvent)
								return (
									<tr key={rowId} className={[css.tbodyRow, isRowClickEnabled ? css.tbodyRowClickable : ''].filter(Boolean).join(' ')}>
										{columns.map((col, colIndex) => {
											const align = col.align || 'left'
											const alignClass = css[`align${align[0].toUpperCase()}${align.slice(1)}` as keyof typeof css]
											const tdClassNames = [css.td, alignClass, isRowClickable && col.clickable !== false ? css.tdClickable : '']
												.filter(Boolean)
												.join(' ')
											const cellInnerClassNames = [css.cellInner, alignClass].filter(Boolean).join(' ')
											return (
												<td
													key={`${rowId}:${col.id}`}
													className={tdClassNames}
													style={{ ...colStyles[colIndex], ...getPinnedStyles(col.id, false) }}
													onClick={(): void => {
														if (col.clickable === false) return
														// Don't treat a text-selection drag that ends in this cell as a row click
														const selection = window.getSelection()
														if (selection && !selection.isCollapsed) return
														handleRowClick(row.original, rowIndex)
													}}
												>
													<div className={cellInnerClassNames} style={colStyles[colIndex]}>
														{renderCell(row.original, rowIndex, col)}
													</div>
												</td>
											)
										})}
									</tr>
								)
							})
						) : (
							<tr>
								<td className={css.emptyState} colSpan={columns.length}>
									{EmptyStateSlot ? (
										<EmptyStateSlot table={tableRuntimeForSlots} />
									) : (
										<div className={`${css.stateCellInner} ${css.emptyStateInner}`}>{i18next.t('components.table.noResultsFound')}</div>
									)}
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			{paginationMode !== 'none' && paginationState.mode !== 'none' && (
				<div className={[css.paginationWrapper, isFooterSticky ? css.paginationSticky : ''].filter(Boolean).join(' ')}>
					<DataTablePagination
						pageSizes={defaultPageSizes}
						rowCount={rowCount}
						pageInfo={paginationState.mode === 'server' ? serverPageInfo ?? { kind: 'unknown' } : undefined}
						pagination={{
							pageIndex: paginationState.mode === 'server' ? paginationState.pageIndex ?? 0 : paginationState.pageIndex,
							pageSize: paginationState.pageSize
						}}
						onPaginationChange={(next): void => {
							commitState(
								{
									...effectiveState,
									pagination:
										paginationMode === 'client'
											? { mode: 'client', pageIndex: next.pageIndex, pageSize: next.pageSize }
											: { mode: 'server', pageIndex: next.pageIndex, pageSize: next.pageSize }
								},
								{ slice: 'pagination' }
							)
						}}
					/>
				</div>
			)}
		</div>
	)
	if (ContainerSlot) {
		return <ContainerSlot>{tableContent}</ContainerSlot>
	}
	return tableContent
}
